/**
 * @fileoverview Custom hook for standardized confirmation dialogs
 * Provides consistent UI for delete, save, and action confirmations
 */
import { useCallback } from 'react';
import Swal from 'sweetalert2';
import { intl } from 'dmsbasereact';

/**
 * Hook for displaying standardized confirmation dialogs
 * @returns {Object} Object containing confirmation dialog functions
 */
export const useConfirmDialog = () => {
  /**
   * Shows a delete confirmation dialog
   * @param {Object} options - Dialog options
   * @param {string} options.title - Dialog title (i18n key)
   * @param {string} options.titleDefault - Default title text
   * @param {string} options.message - Confirmation message (i18n key)
   * @param {string} options.messageDefault - Default message text
   * @param {string} options.itemName - Name of item being deleted
   * @returns {Promise<boolean>} True if confirmed, false otherwise
   */
  const confirmDelete = useCallback(async ({
    title,
    titleDefault,
    message,
    messageDefault,
    itemName
  }) => {
    const result = await Swal.fire({
      title: intl.get(title).d(titleDefault),
      html: `<p>${intl.get(message).d(messageDefault)}</p>${itemName ? `<p><strong>${itemName}</strong></p>` : ''}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: intl.get('Delete').d('Delete'),
      cancelButtonText: intl.get('Cancel').d('Cancel'),
    });
    return result.isConfirmed;
  }, []);

  /**
   * Shows a save confirmation dialog
   * @param {Object} options - Dialog options
   * @param {string} options.title - Dialog title (i18n key)
   * @param {string} options.titleDefault - Default title text
   * @param {string} options.message - Confirmation message (i18n key)
   * @param {string} options.messageDefault - Default message text
   * @returns {Promise<boolean>} True if confirmed, false otherwise
   */
  const confirmSave = useCallback(async ({
    title = 'ConfirmSave',
    titleDefault = 'Confirm Save',
    message = 'ConfirmSaveMessage',
    messageDefault = 'Are you sure you want to save the changes?'
  }) => {
    const result = await Swal.fire({
      title: intl.get(title).d(titleDefault),
      text: intl.get(message).d(messageDefault),
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#6c757d',
      confirmButtonText: intl.get('Save').d('Save'),
      cancelButtonText: intl.get('Cancel').d('Cancel'),
    });
    return result.isConfirmed;
  }, []);

  /**
   * Shows a generic action confirmation dialog
   * @param {Object} options - Dialog options
   * @param {string} options.title - Dialog title
   * @param {string} options.message - Confirmation message
   * @param {string} options.icon - Swal icon type ('warning', 'question', 'info')
   * @param {string} options.confirmText - Confirm button text
   * @param {string} options.confirmColor - Confirm button color
   * @returns {Promise<boolean>} True if confirmed, false otherwise
   */
  const confirmAction = useCallback(async ({
    title,
    message,
    icon = 'question',
    confirmText,
    confirmTextDefault = 'Confirm',
    confirmColor = '#3085d6'
  }) => {
    const result = await Swal.fire({
      title,
      text: message,
      icon,
      showCancelButton: true,
      confirmButtonColor: confirmColor,
      cancelButtonColor: '#6c757d',
      confirmButtonText: intl.get(confirmText).d(confirmTextDefault),
      cancelButtonText: intl.get('Cancel').d('Cancel'),
    });
    return result.isConfirmed;
  }, []);

  /**
   * Shows a success notification
   * @param {string} title - Success title
   * @param {string} message - Success message
   */
  const showSuccess = useCallback((title, message) => {
    Swal.fire({
      title,
      text: message,
      icon: 'success',
      timer: 2000,
      showConfirmButton: false
    });
  }, []);

  /**
   * Shows an error notification
   * @param {string} title - Error title
   * @param {string} message - Error message
   */
  const showError = useCallback((title, message) => {
    Swal.fire({
      title,
      text: message,
      icon: 'error',
      confirmButtonColor: '#3085d6'
    });
  }, []);

  /**
   * Shows a warning notification with a single confirm button
   * @param {Object} options - Dialog options
   * @param {string} options.title - Warning title
   * @param {string} options.message - Warning message
   * @param {string} options.confirmText - Confirm button text (i18n key)
   * @param {string} options.confirmTextDefault - Default confirm button text
   * @returns {Promise<boolean>} True when confirmed
   */
  const showWarning = useCallback(async ({
    title,
    message,
    confirmText = 'Ok',
    confirmTextDefault = 'Ok'
  }) => {
    const result = await Swal.fire({
      title,
      text: message,
      icon: 'warning',
      confirmButtonColor: '#3085d6',
      confirmButtonText: intl.get(confirmText).d(confirmTextDefault),
      allowOutsideClick: false,
      allowEscapeKey: false
    });
    return result.isConfirmed;
  }, []);

  return {
    confirmDelete,
    confirmSave,
    confirmAction,
    showSuccess,
    showError,
    showWarning
  };
};

export default useConfirmDialog;
